import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_USERS } from '../../graphql/queries';
import { getEligibleUsers, getAreaDisplayName } from './utils/jobUtils';
import AssignmentAvailabilityWarning from '../team-availability/AssignmentAvailabilityWarning';

/**
 * User assignment component for selecting job assignee
 * @param {Object} props - Component props
 * @param {string} props.assignedToId - Currently assigned user ID
 * @param {Function} props.onAssignedToIdChange - Assignment change handler
 * @param {string} props.category - Job category for filtering eligible users
 * @param {Object} props.selectedProperty - Selected property with area information
 * @param {Object} props.selectedServiceType - Selected service type with allowed roles
 * @param {string|Date} props.scheduledDate - Scheduled date of the job (used to check assignee availability)
 * @param {Object} props.errors - Validation errors
 * @returns {JSX.Element} UserAssignment component
 */
export default function UserAssignment({ 
  assignedToId, 
  onAssignedToIdChange, 
  category,
  selectedProperty,
  selectedServiceType,
  scheduledDate = null,
  errors = {} 
}) {
  const { data: usersData, loading: usersLoading } = useQuery(GET_USERS);

  // Get eligible users based on category, property area, and service type
  // For custom jobs, include admin roles (ADMIN, GUEST_APP_MANAGER) if they're assigned to the property area
  const eligibleUsers = getEligibleUsers(
    usersData?.users, 
    category, 
    selectedProperty, 
    selectedServiceType,
    true // includeAdmins = true for custom jobs
  ).sort((a, b) => {
    // Extract first names for sorting
    const getFirstName = (user) => {
      if (user.firstName) {
        return user.firstName.toLowerCase();
      }
      // If no firstName, extract from name field
      if (user.name) {
        return user.name.split(' ')[0].toLowerCase();
      }
      return '';
    };
    
    const firstNameA = getFirstName(a);
    const firstNameB = getFirstName(b);
    
    return firstNameA.localeCompare(firstNameB);
  });

  // Get all users for validation
  const allUsers = usersData?.users || [];
  const totalActiveUsers = allUsers.filter(user => user.isActive !== false).length;

  const availabilityUserIds = useMemo(
    () => (assignedToId ? [assignedToId] : []),
    [assignedToId]
  );
  const availabilityDates = useMemo(
    () => (scheduledDate ? [scheduledDate] : []),
    [scheduledDate]
  );

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Assign To
      </label>
      
      <select
        value={assignedToId || ''}
        onChange={(e) => onAssignedToIdChange?.(e.target.value)}
        className={`w-full p-2 border rounded-md ${errors.assignedToId ? 'border-red-500' : ''}`}
        disabled={usersLoading}
      >
        <option value="">Unassigned</option>
        {eligibleUsers.map(user => (
          <option key={user.id} value={user.id}>
            {user.name} ({user.role})
          </option>
        ))}
      </select>
      
      {errors.assignedToId && (
        <p className="mt-1 text-xs text-red-500">{errors.assignedToId}</p>
      )}

      <AssignmentAvailabilityWarning
        userIds={availabilityUserIds}
        dates={availabilityDates}
      />

      {/* No eligible users warning */}
      {!usersLoading && eligibleUsers.length === 0 && totalActiveUsers > 0 && (
        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-xs text-yellow-800">
            <strong>No eligible users found.</strong>
            {selectedProperty?.area && (
              <span> No users (including admins) are assigned to {getAreaDisplayName(selectedProperty.area)}.</span>
            )}
            {selectedServiceType?.allowedRoles && (
              <span> No users have the required roles: {selectedServiceType.allowedRoles.join(', ')} or admin roles.</span>
            )}
          </p>
          <p className="text-xs text-yellow-700 mt-1">
            Try selecting a different property or service type to see more users.
          </p>
        </div>
      )}
      
      {/* Loading state */}
      {usersLoading && (
        <p className="mt-1 text-xs text-blue-500">Loading users...</p>
      )}
      

    </div>
  );
} 